import { useMemo } from "react";
import { useIntl } from "react-intl";
import { basicPlanId, freePlanId, proPlanId } from "../core/variables";

const usePlanIntroduction = () => {
    const {locale} = useIntl();

    const downgradeDescription = useMemo<DowngradeDescription[]>(() => {
        switch (locale) {
            case "en":
                return enDowngradeDescription
            case "ko":
                return koDowngradeDescription
            default:
                return []
        }
    }, [locale])

    const planDetails = useMemo<PlanDetail[]>(() => {
        switch (locale) {
            case "en":
                return enPlanDetails
            case "ko":
                return koPlanDetails
            default:
                return []
        }
    }, [locale])

    const planIntroduction = useMemo<any[]>(() => {
        switch (locale) {
            case "en":
                return enPlanIntroduction
            case "ko":
                return koPlanIntroduction
            default:
                return []
        }
    }, [locale])

    return {
        downgradeDescription,
        planDetails,
        planIntroduction
    }
};

export interface DowngradeDescriptionItem {
    title: string
    description: string
    warning?: string
}

export interface DowngradeDescription {
    previousPlanId: string;
    planDowngradeId: string;
    descriptions: DowngradeDescriptionItem[];
}

//TODO message를 현재 사용량에서 average로 바꾸어야 함
const koDowngradeDescription: DowngradeDescription[] = [
    {
        previousPlanId: proPlanId!,
        planDowngradeId: basicPlanId!,
        descriptions: [
            {
                title: "월 7,000건의 추가 메시지 사용량 제공",
                description: "Basic 플랜에서는 월 3,000건의 메시지가 제공됩니다.",
                warning: "{channelName} 채널의 최대 메시지 사용량은 {message}건으로, 사용량 초과 시 챗봇 이용이 제한될 수 있습니다."
            },
            {
                title: "추가 웹사이트, 문서, Notion 연동(최대 300Mb)",
                description: "Basic 플랜에서는 한 개의 기본 웹사이트만 연동할 수 있습니다.",
                warning: "{channelName} 채널에 추가된 4개의 데이터소스는 다음 달부터 자동으로 비활성화합니다."
            },
            {
                title: "외부 뉴스 데이터 연동",
                description: "Basic 플랜에서는 외부 뉴스 데이터 연동 기능이 제한됩니다."
            },
        ]
    }
]

const enDowngradeDescription: DowngradeDescription[] = [
    {
        previousPlanId: proPlanId!,
        planDowngradeId: basicPlanId!,
        descriptions: [
            {
                title: "Additional Message Usage: 7,000 messages per month",
                description: "The Basic plan provides 3,000 messages per month.",
                warning: "The maximum message usage for the {channelName} channelId is {message}. Exceeding this limit may result in restrictions on chatbot usage."
            },
            {
                title: "Additional Website, Document, Notion Integration (up to 300Mb)",
                description: "The Basic plan allows integration with one default website only.",
                warning: "The 4 additional datasources added to the {channelName} channelId will be automatically disabled starting next month."
            },
            {
                title: "External News Data Integration",
                description: "The Basic plan limits the functionality of external news data integration."
            }
        ]
    }
]



type PlanInfo = {
    text: string,
    sub?: string
}

type costInfo = {
    month: number,
    year: number,
}

export type PlanDetail = {
    _id: string,
    name?: string,
    cost?: costInfo
    info: (string | PlanInfo)[]
}

export const enPlanDetails: PlanDetail[] = [
    {
        _id: freePlanId!,
        name: "free",
        info: [
            {text: "50 messages"},
            {text: "1 website", sub: "(up to 1Mb)"},
            {text: "LLM-based Q&A service"},
            {text: "Statistics feature"},
            {text: "Monitoring feature"}
        ]
    },
    {
        _id: basicPlanId!,
        name: "basic",
        cost: {
            month: 30,
            year: 330
        },
        info: [
            {text: "3,000 messages"},
            {text: "1 website", sub: "(up to 100Mb)"},
            {text: "LLM-based Q&A service"},
            {text: "Statistics feature"},
            {text: "Monitoring feature"}
        ]
    },
    {
        _id: proPlanId!,
        name: "pro",
        cost: {
            month: 90,
            year: 990
        },
        info: [
            {text: "10,000 messages"},
            {text: "Website, Document, Notion", sub: "(up to 300Mb)"},
            {text: "External news data integration"},
            {text: "LLM-based Q&A service"},
            {text: "Statistics feature"},
            {text: "Monitoring feature"},
            {text: "Basic customer service"}
        ]
    }
]


export const koPlanDetails: PlanDetail[] = [
    {
        _id: freePlanId!,
        name: "free",
        info:[
            {text: "메시지 50개"},
            {text: "웹사이트 1개", sub: "(최대 1Mb)"},
            {text: "LLM 기반 질의응답 서비스"},
            {text: "통계 기능 제공"},
            {text: "모니터링 기능 제공"}]
    },
    {
        _id: basicPlanId!,
        name: "basic",
        cost: {
            month: 30000,
            year: 330000
        },
        info: [
            {text: "메시지 3,000개"},
            {text: "웹사이트 1개", sub: "(최대 100Mb)"},
            {text: "LLM 기반 질의응답 서비스"},
            {text: "통계 기능 제공"},
            {text: "모니터링 기능 제공"}]
    },
    {
        _id: proPlanId!,
        name: "pro",
        cost: {
            month: 90000,
            year: 990000
        },
        info: [
            {text: "메시지 10,000개"},
            {text: "웹사이트, 문서, Notion", sub: "(최대 300Mb)"},
            {text: "외부 뉴스 데이터 연동"},
            {text: "LLM 기반 질의응답 서비스"},
            {text: "통계 기능 제공"},
            {text: "모니터링 기능 제공"},
            {text: "기본 고객 서비스"}]
    }
]


export const koPlanIntroduction = [
    {
        _id: basicPlanId,
        introduction: "월 3,000건의 메시지, 100Mb의 데이터 소스 저장 공간이 제공됩니다."
    },
    {
        _id: proPlanId,
        introduction: "월 10,000건의 메시지, PDF/PPT/Word/Notion 등 추가 데이터소스 연동 등의 기능을 더욱 다양하게 활용해보세요."
    }
]

export const enPlanIntroduction = [
    {
        _id: basicPlanId,
        introduction: "3,000 messages per month and 100MB of data source storage space will be provided"
    },
    {
        _id: proPlanId,
        introduction: "Utilize various functions such as 10,000 messages per month, additional data source integration including PDF/PPT/Word/Notion, and more diversely."
    }
]


export default usePlanIntroduction;
